import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
    Box,
    Button as MuiButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { Button as FoodsButton, FoodsIconButton, Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";
import { useSnackbar } from "src/utils/useSnackbar";

import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportAppearance,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";
import { IngredientSupplierTransportFields } from "./IngredientSupplierTransportEditor.internal";
import { useSaveIngredientSupplierTransports } from "./useSaveIngredientSupplierTransports.internal";

type IngredientSupplierTransportDialogProps = {
    appearance?: IngredientSupplierTransportAppearance;
    open: boolean;
    onClose: () => void;
    title: string;
    ingredientSupplierId?: number;
    value?: IngredientSupplierTransportSelection;
    onChange: (next: IngredientSupplierTransportSelection, reason: IngredientSupplierTransportChangeReason) => void;
    saveMode?: "deferred" | "immediate";
};

export const IngredientSupplierTransportDialog = ({
    appearance = "legacy",
    open,
    onClose,
    title,
    ingredientSupplierId,
    value = {},
    onChange,
    saveMode = "immediate",
}: IngredientSupplierTransportDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isStageEnabled } = useEnabledTransportStages();
    const saveIngredientSupplierTransports = useSaveIngredientSupplierTransports();
    const snackbar = useSnackbar();
    const [draft, setDraft] = useState<IngredientSupplierTransportSelection>(value);
    const [draftChangeReason, setDraftChangeReason] = useState<IngredientSupplierTransportChangeReason>("hydrate");
    const [isSaving, setIsSaving] = useState(false);
    const initializedDialogKey = useRef<string | null>(null);
    const activeSaveRequest = useRef(0);
    const saving = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
            activeSaveRequest.current += 1;
        };
    }, []);

    useEffect(() => {
        if (!open) {
            activeSaveRequest.current += 1;
            saving.current = false;
            setIsSaving(false);
            initializedDialogKey.current = null;
            setDraftChangeReason("hydrate");
            return;
        }

        const dialogKey = `${ingredientSupplierId ?? "new"}:${title}`;
        if (initializedDialogKey.current !== dialogKey) {
            activeSaveRequest.current += 1;
            saving.current = false;
            setIsSaving(false);
            initializedDialogKey.current = dialogKey;
            setDraft(value);
            setDraftChangeReason("hydrate");
        }
    }, [ingredientSupplierId, open, title, value]);

    const enabledIngredientStages = useMemo(
        () => INGREDIENT_LEVEL_STAGES.filter((stage) => isStageEnabled(stage)),
        [isStageEnabled],
    );
    const hasMissingRequiredCircuit = enabledIngredientStages.some((stage) => draft[stage]?.transportCircuit == null);

    const validate = async () => {
        if (saving.current) {
            return;
        }

        if (saveMode === "deferred" || ingredientSupplierId === undefined) {
            onChange(draft, draftChangeReason);
            onClose();
            return;
        }

        saving.current = true;
        const saveRequest = activeSaveRequest.current + 1;
        activeSaveRequest.current = saveRequest;
        setIsSaving(true);
        try {
            const persistedDraft = await saveIngredientSupplierTransports(
                `/api/ingredient_suppliers/${ingredientSupplierId}` as IRI,
                draft,
            );

            if (!mounted.current || activeSaveRequest.current !== saveRequest) {
                return;
            }

            saving.current = false;
            setIsSaving(false);
            onChange(persistedDraft, draftChangeReason);
            onClose();
        } catch {
            if (!mounted.current || activeSaveRequest.current !== saveRequest) {
                return;
            }

            saving.current = false;
            setIsSaving(false);
            snackbar.setSnackbarProps({
                type: "error",
                message: t(
                    "ingredientSupplier.transport.saveError",
                    "The transport circuits could not be saved. Please try again.",
                ),
            });
        }
    };

    const closeDialog = () => {
        if (!saving.current) {
            onClose();
        }
    };

    const setDialogOpen: Dispatch<SetStateAction<boolean>> = (nextOpen) => {
        const resolvedOpen = typeof nextOpen === "function" ? nextOpen(open) : nextOpen;
        if (!resolvedOpen) closeDialog();
    };

    const updateDraft = (
        next: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => {
        setDraft(next);
        if (reason === "user") {
            setDraftChangeReason("user");
        }
    };

    if (appearance === "legacy") {
        return (
            <Dialog
                open={open}
                onClose={closeDialog}
                disableEscapeKeyDown={isSaving}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        maxWidth: theme.spacing(92),
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.custom.grey[100],
                        boxShadow: theme.custom.shadows.grey,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        alignItems: "center",
                        borderBottom: `${theme.spacing(0.125)} solid ${theme.custom.grey[500]}`,
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: theme.spacing(8),
                        p: theme.spacing(2, 2, 2, 3),
                    }}
                >
                    <Stack gap={theme.spacing(0.5)}>
                        <MuiTypography
                            component="h2"
                            variant="h5"
                            sx={{
                                fontSize: theme.spacing(2),
                                fontWeight: theme.typography.fontWeightBold,
                            }}
                        >
                            {t("ingredientSupplier.transport.modalTitle", "Configure transport")}
                        </MuiTypography>
                        <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                            {title}
                        </MuiTypography>
                    </Stack>
                    <FoodsIconButton
                        size="m"
                        icon="close"
                        buttonProps={{
                            "aria-label": t("common.close", "Close"),
                            disabled: isSaving,
                            onClick: closeDialog,
                        }}
                    />
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <IngredientSupplierTransportFields
                        appearance="legacy"
                        ingredientSupplierId={ingredientSupplierId}
                        value={draft}
                        onChange={updateDraft}
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: `${theme.spacing(0.125)} solid ${theme.custom.grey[500]}`,
                        p: theme.spacing(2),
                    }}
                >
                    <MuiButton onClick={closeDialog} disabled={isSaving}>
                        {t("primarydialog.cancel", "Cancel")}
                    </MuiButton>
                    <MuiButton variant="contained" onClick={validate} disabled={hasMissingRequiredCircuit || isSaving}>
                        {t("primarydialog.validate", "Validate")}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <FoodsBottomSheetDialog
            open={open}
            setOpen={setDialogOpen}
            disableEscapeKeyDown={isSaving}
            PaperProps={{
                sx: {
                    maxWidth: theme.spacing(92),
                    borderRadius: theme.spacing(1.5),
                    backgroundColor: theme.custom.grey[100],
                    boxShadow: theme.custom.shadows.grey,
                    overflow: "hidden",
                    "& .MuiDialogTitle-root": {
                        minHeight: theme.spacing(8),
                        p: 0,
                    },
                    "& .MuiDialogTitle-root > .MuiButton-root": {
                        display: "none",
                    },
                },
            }}
            title={
                <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        boxSizing: "border-box",
                        minHeight: theme.spacing(8),
                        p: theme.spacing(2, 2, 2, 3),
                        width: "100%",
                    }}
                >
                    <Stack gap={0.5}>
                        <FoodsTypography
                            component="h2"
                            variant="h5"
                            sx={{
                                fontSize: theme.spacing(2),
                                fontWeight: theme.typography.fontWeightBold,
                            }}
                        >
                            {t("ingredientSupplier.transport.modalTitle", "Configure transport")}
                        </FoodsTypography>
                        <FoodsTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                            {title}
                        </FoodsTypography>
                    </Stack>
                    <FoodsIconButton
                        size="m"
                        icon="close"
                        buttonProps={{
                            "aria-label": t("common.close", "Close"),
                            disabled: isSaving,
                            onClick: closeDialog,
                        }}
                    />
                </Stack>
            }
            content={
                <Stack sx={{ minHeight: 0 }}>
                    <Box sx={{ minHeight: 0, overflow: "auto" }}>
                        <IngredientSupplierTransportFields
                            appearance="product-v2"
                            ingredientSupplierId={ingredientSupplierId}
                            value={draft}
                            onChange={updateDraft}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        gap={1}
                        sx={{
                            borderTop: `${theme.spacing(0.125)} solid ${theme.custom.grey[500]}`,
                            p: 2,
                        }}
                    >
                        <FoodsButton variant="ternary" onClick={closeDialog} disabled={isSaving}>
                            {t("primarydialog.cancel", "Cancel")}
                        </FoodsButton>
                        <FoodsButton
                            variant="primary"
                            onClick={validate}
                            disabled={hasMissingRequiredCircuit || isSaving}
                        >
                            {t("primarydialog.validate", "Validate")}
                        </FoodsButton>
                    </Stack>
                </Stack>
            }
        />
    );
};
